import { Box } from 'braid-design-system';
import React, { useEffect, useRef, useState } from 'react';

import { DropdownBackdrop } from './DropdownBackdrop';
import { DropdownMenuItems } from './DropdownMenuItems';
import { Trigger } from './Trigger';
import { attachPropsToChild } from './utils/attachPropsToChild';
import { getKeyboardActions } from './utils/getKeyboardActions';

// Bug: Without the dropdown menu being rendered via ReactPortal, the table will cut off partial/entire dropdown menu
// Customising an OverflowMenu component here (not using Braid OverflowMenu)
// is because Braid OverflowMenu doesn't work well with BraidPortal
// TODO: Convert to Braid OverflowMenu once it supports or integrates with BraidPortal

interface OverflowMenuProps {
  children: React.ReactNode;
  label: string;
  menuWidth: number;
  onOpen?: () => void;
}

export const OverflowMenu = ({
  children,
  label,
  menuWidth,
  onOpen,
}: OverflowMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuItemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const closeMenu = () => {
    setIsMenuOpen(false);
    triggerRef.current?.focus();
  };

  const { onMenuItemKeyDown, onTriggerKeyDown } = getKeyboardActions({
    menuItemRefs,
    menuItemCount: React.Children.count(children),
    closeMenu,
  });

  useEffect(() => {
    if (isMenuOpen) {
      onOpen?.();
    }
  }, [isMenuOpen, onOpen]);

  const toggleIsOpen = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const onTriggerClick = () => {
    toggleIsOpen();
  };

  const onMenuItemsClick = () => {
    closeMenu();
  };

  const onBackdropClick = () => {
    closeMenu();
  };

  return (
    <Box
      style={{
        position: 'relative',
        display: 'inline-block',
      }}
    >
      <Trigger
        isActive={isMenuOpen}
        label={label}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
        triggerRef={triggerRef}
      />

      {isMenuOpen && (
        <>
          <DropdownBackdrop onClick={onBackdropClick} />
          <DropdownMenuItems
            menuAlignment="BOTTOM_RIGHT"
            menuWidth={menuWidth}
            onClick={onMenuItemsClick}
            triggerRef={triggerRef}
          >
            {/* Children is each menu items */}
            {/* Current they are coming from getTrackingMenuItem */}
            {/* The code below is to avoid refactoring it */}
            {React.Children.map(children, (child, index) =>
              attachPropsToChild({
                child,
                props: {
                  ref: (el: HTMLButtonElement | null) => {
                    menuItemRefs.current[index] = el;
                  },
                  onKeyDown: onMenuItemKeyDown,
                },
              }),
            )}
          </DropdownMenuItems>
        </>
      )}
    </Box>
  );
};
